import { MessageDescriptor } from 'react-intl'

import { CountryNameToISOCode } from '../../intl/constants'
import { MEMBER_PREFERENCE_IDS, S4C_PROGRAM_OPTION_FLOW } from '../../memberPreferences/constants'

export interface RenderCondition {
  target: RENDER_CONDITION_TARGETS
  path?: string // e.g. target[path], else just check target
  fn: (val: any) => boolean // comparison function
}

export interface ExpandCondition {
  target: SHOULD_EXPAND_ACCORDION_CONDITION_TARGETS
  fn: (val: any) => boolean // comparison function
  defaultValueId?: MEMBER_PREFERENCE_IDS // the value id that needs to be selected when accordion expands
  isCollapseDisabled?: boolean // if the accordion is expanded, isCollapseDisabled can be used to always keep the accordion open by removing the functionality that allows the user to collapse/expand the accordion
}

// Currrently the only recognized targets for conditional expression checks. We have to adhere to whatever
// targets and rules are defined so that there isn't disconnect between metadata and application layer logic
export enum RENDER_CONDITION_TARGETS {
  SELECTED_PREFERENCES = 'selectedPreferences',
  FEATURE_FLAG = 'featureFlag',
  PRODUCT_TYPE = 'productType',
  THEME = 'theme',
}

export enum INFO_OVERLAY_TARGET_FNS {
  PRESS_GENDER_PREF_DISCLAIMER = 'PRESS_GENDER_PREF_DISCLAIMER',
  PRESS_CARE_TEAM_LINK = 'PRESS_CARE_TEAM_LINK',
}

// Determine whether to render options within accordion and checkbox or radio buttons
export enum OPTIONS_TYPE {
  CHECKBOX = 'CHECKBOX',
  RADIO = 'RADIO',
}

// Determine whether a member preference accordion should be expanded upon visiting the page
export enum SHOULD_EXPAND_ACCORDION_CONDITION_TARGETS {
  WHEN_USER_VISITS_PREFERENCES_FROM_CONNECTED_CALENDAR = 'WHEN_USER_VISITS_PREFERENCES_FROM_CONNECTED_CALENDAR',
  WHEN_USER_IS_INTERNATIONAL_AND_EXPANDED_PREFERENCES_DISABLED = 'WHEN_USER_IS_INTERNATIONAL_AND_EXPANDED_PREFERENCES_DISABLED',
}

// Component that is only rendered when the option id is currently selected
// Must also define componentName in COMPONENT_MAP to successfully export component
export interface ConditionalChildComponent {
  componentName: string
  optionId: MEMBER_PREFERENCE_IDS
}

export interface PreferenceMetadataConfig {
  name: string
  title: {
    defaultMessage: string
    description: string
  }
  countryConfig: {
    [key in (typeof CountryNameToISOCode)[keyof typeof CountryNameToISOCode]]?: CountryPreferenceConfig
  }
  optionType: OPTIONS_TYPE
  conditionalChildComponent?: ConditionalChildComponent
  // Determine when accordion should be expanded upon visiting the page
  expandConditions?: ExpandCondition[]
  // In addition to countryConfig rules, must satisfy expressions listed here to show the accordion
  renderConditions?: RenderCondition[]
  icon: string
  promptText?: {
    defaultMessage: string
    description: string
  }
}

// Configuration for each selectable option within a preference category
export interface PreferenceSelectionOption {
  text: MessageDescriptor
  id: MEMBER_PREFERENCE_IDS
  // Optional property to override the text that is shown when the option is selected, e.g. "Veterans" instead of "Experience working with veterans"
  selectedTextLabel?: MessageDescriptor
  defaultValue?: string[]
  shouldRenderSubtitle?: (val: any) => boolean
  // Determine whether option should be selected upon visiting the page
  shouldToggleDefaultOption?: (val: any) => boolean
  description?: {
    value: {
      defaultMessage: string
      description: string
    }
  }[]
  // Optional property to render inline text within an option instead of the footer
  inlineText?: PreferenceMetadataConfigAdditionalText
}

// Country-specific configuration support for each preference category
export interface CountryPreferenceConfig {
  programOptionFlows: S4C_PROGRAM_OPTION_FLOW[]
  options: PreferenceSelectionOption[]
  footerText?: PreferenceMetadataConfigAdditionalText
  title?: {
    defaultMessage: string
    description: string
  } // This title is used to override the default title for a specific country
  promptText?: {
    defaultMessage: string
    description: string
  } // This promptText is used to override the default promptText for a specific country
}

// Optional additional text configuration for each preference category (can be used for footer text or inline text)
export interface PreferenceMetadataConfigAdditionalText {
  message?: {
    defaultMessage: string
    description: string
  }
  infoOverlay?: {
    onPress?: {
      fn: INFO_OVERLAY_TARGET_FNS
    }
    content: {
      defaultMessage: string
      description: string
    }
    linkConfig?: {
      // Optional field -- Support links within the info overlay body
      onPress: {
        fn: INFO_OVERLAY_TARGET_FNS // Function to call when the link is pressed
        params: any // Pass into the function call
      }
    }
  }
  // Some additional texts only render after the value has been selected
  renderConditions?: RenderCondition[]
  color: string
}
